import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiCall } from "services/api";
import { paramParsify } from "utils/queryParams";
import storage from "services/storage";

export const useGetShareholderList = ({
  enableAutoFetchByParams,
  params = {},
}) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(
    fetchShareholderList(
      setLoading,
      queryParams,
      params,
      setResponse,
      setError,
    ),
    [queryParams],
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(enableAutoFetchByParams
      ? [
          String(queryParams?.page),
          String(queryParams?.search),
          String(queryParams?.sort_by),
          String(queryParams?.sort_order),
        ]
      : []),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export function fetchShareholderList(
  setLoading,
  queryParams,
  params,
  setResponse,
  setError,
) {
  const id = storage.getCompanyID();
  return async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/affiliated-shareholders`,
        queryParams: {
          sort_by: "effective_date",
          sort_order: "desc",
          ...queryParams,
          ...params,
        },
        method: "GET",
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
}

export function updateShareholder(
  setLoading,
  sid,
  body,
  setResponse,
  setError,
) {
  const id = storage.getCompanyID();
  return async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/shareholders/${sid}`,
        method: "PUT",
        data: body,
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
}

export function addShareholder(setLoading, body, setResponse, setError) {
  const id = storage.getCompanyID();
  return async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/shareholders`,
        method: "POST",
        data: body,
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
}

export function getShareholderLog(
  setLoading,
  queryParams,
  setResponse,
  setError,
) {
  const id = storage.getCompanyID();
  if (!queryParams["limit"]) {
    queryParams["limit"] = 5;
  }
  if (!queryParams["start_date"]) {
    queryParams["start_date"] = getDateSevenDaysAgo();
  }
  return async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/affiliated-shareholders/logs`,
        queryParams: {
          ...queryParams,
        },
        method: "GET",
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
}

function getDateSevenDaysAgo() {
  const today = new Date();
  const sevenDaysAgo = new Date(today.setDate(today.getDate() - 7));

  const year = sevenDaysAgo.getFullYear();
  const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const day = String(sevenDaysAgo.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
